import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './AuthContext';
import Dashboard from './pages/Dashboard';
import Indicators from './pages/Indicators';
import Pillar from './pages/Pillar';
import Observations from './pages/Observations';
import Score from './pages/Score';
import AINews from './pages/AINews';
import Shock from './pages/Shock';
import MacroForecast from './pages/MacroForecast';
import DashboardMacroForecast from './pages/DashboardMacroForecast';
import MacroOutlook from './pages/MacroOutlook';
import SaturationDashboard from './pages/SaturationDashboard';
import MacroImpulseDashboard from './pages/MacroImpulseDashboard';
import Level1Dashboard from './pages/Level1Dashboard';
import StocksList from './pages/StocksList';
import StockDetail from './pages/StockDetail';
import Recommendations from './pages/Recommendations';
import StockScanner from './pages/StockScanner';
import Alerts from './pages/Alerts';
import DowntrendDetection from './pages/DowntrendDetection';
import DownshiftScanner from './pages/DownshiftScanner';
import EasyStepScanner from './pages/EasyStepScanner';
import PerformanceDashboard from './pages/PerformanceDashboard';
import SectorsManagement from './pages/SectorsManagement';
import RiskManagement from './pages/RiskManagement';
import TradingDecision from './pages/TradingDecision';
import PortfolioHealth from './pages/PortfolioHealth';
import PortfolioConstruction from './pages/PortfolioConstruction';
import PortfolioRecommendations from './pages/PortfolioRecommendations';
import AutoPick from './pages/AutoPick';
import SafeInvest from './pages/SafeInvest';
import RegimeWatchlist from './pages/RegimeWatchlist';
import RiskOpportunityMatrix from './pages/RiskOpportunityMatrix';
import PaperTrading from './pages/PaperTrading';
import HighVolDashboard from './pages/HighVolDashboard';
import ReversalCatcherScanner from './pages/ReversalCatcherScanner';
import EarlyPivotScanner from './pages/EarlyPivotScanner';
import CalibrationDashboard from './pages/CalibrationDashboard';
import SectorRotation from './pages/SectorRotation';
import ICBIndustry from './pages/ICBIndustry';
import MarketCorrection from './pages/MarketCorrection';
import MarketRiskUnified from './pages/MarketRiskUnified';
import ModelAccuracy from './pages/ModelAccuracy';
import MarketRecovery from './pages/MarketRecovery';
import HedgeDetection from './pages/HedgeDetection';
import MarketSentiment from './pages/MarketSentiment';
import ConcentrationAllocation from './pages/ConcentrationAllocation';
import TrendPredictions from './pages/TrendPredictions';
import TPlus from './pages/TPlus';
import PatternCluster from './pages/PatternCluster';
import StockPhases from './pages/StockPhases';
import RiskMLDashboard from './pages/RiskMLDashboard';
import RiskMLDetail from './pages/RiskMLDetail';
import UpsideMLDashboard from './pages/UpsideMLDashboard';
import UpsideMLDetail from './pages/UpsideMLDetail';
import LegendaryHub from './pages/LegendaryHub';
import LegendaryONeil from './pages/LegendaryONeil';
import LegendaryMinervini from './pages/LegendaryMinervini';
import LegendaryWeinstein from './pages/LegendaryWeinstein';
import LegendaryTurtle from './pages/LegendaryTurtle';
import LegendaryLivermore from './pages/LegendaryLivermore';
import MoneyFlow from './pages/MoneyFlow';
import DistributionDetector from './pages/DistributionDetector';
import VpaPage from './pages/VPA';
import VsaPage from './pages/VSA';
import SurgeReadiness from './pages/SurgeReadiness';
import StockStrength from './pages/StockStrength';
import IchimokuTransition from './pages/IchimokuTransition';
import BehaviorLabels from './pages/BehaviorLabels';
import SilentAccumulation from './pages/SilentAccumulation';
import RiskCompositeDashboard from './pages/RiskCompositeDashboard';
import NicheRegimeDashboard from './pages/NicheRegimeDashboard';
import MarketDivergence from './pages/MarketDivergence';
import MarketResilience from './pages/MarketResilience';
import SmartMoneyUnified from './pages/SmartMoneyUnified';
import DuplicateSignature from './pages/DuplicateSignature';
import BacktestValidation from './pages/BacktestValidation';
import SmartMoneyCommandCenter from './pages/SmartMoneyCommandCenter';
import EarlyDivergenceWatchlist from './pages/EarlyDivergenceWatchlist';
import DetectorPaperTrading from './pages/DetectorPaperTrading';
import DetectorRecommendations from './pages/DetectorRecommendations';
import PivotPoints from './pages/PivotPoints';
import DarvasBox from './pages/DarvasBox';
import MorningBrief from './pages/MorningBrief';
import AutomationCenter from './pages/AutomationCenter';
import MarketPhaseDashboard from './pages/MarketPhaseDashboard';
import BreakoutCandidates from './pages/BreakoutCandidates';
import PreparationPhase from './pages/PreparationPhase';
import EarlyAccumulationDashboard from './pages/EarlyAccumulationDashboard';
import BottomingFlowMonitor from './pages/BottomingFlowMonitor';
import MarketInternalRadar from './pages/MarketInternalRadar';
import StatisticalDashboard from './pages/StatisticalDashboard';
import LandingAuthRedirect from './pages/LandingAuthRedirect';
import Pricing from './pages/Pricing';
import theme from './theme';

// Create a client with optimized settings for caching and back navigation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - cache persists (renamed from cacheTime in v5)
      refetchOnWindowFocus: false, // Don't refetch on tab switch
      refetchOnMount: false, // Don't refetch on component mount if data exists
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1,
    },
  },
});

function App() {
  const router = createBrowserRouter(
    [
      {
        path: "/login",
        element: <LandingAuthRedirect />,
      },
      {
        path: "/login/email",
        element: <LandingAuthRedirect />,
      },
      {
        path: "/register",
        element: <LandingAuthRedirect />,
      },
      {
        path: "/",
        element: <Layout />,
        children: [
        {
          index: true,
          element: (
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "pricing",
          element: (
            <PrivateRoute>
              <Pricing />
            </PrivateRoute>
          ),
        },
        {
          path: "morning-brief",
          element: (
            <PrivateRoute>
              <MorningBrief />
            </PrivateRoute>
          ),
        },
        {
          path: "automation-center",
          element: (
            <PrivateRoute>
              <AutomationCenter />
            </PrivateRoute>
          ),
        },
        {
          path: "market-phase-dashboard",
          element: (
            <PrivateRoute>
              <MarketPhaseDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "score",
          element: (
            <PrivateRoute>
              <Score />
            </PrivateRoute>
          ),
        },
        {
          path: "pillar",
          element: (
            <PrivateRoute>
              <Pillar />
            </PrivateRoute>
          ),
        },
        {
          path: "indicators",
          element: (
            <PrivateRoute>
              <Indicators />
            </PrivateRoute>
          ),
        },
        {
          path: "news-radar",
          element: (
            <PrivateRoute>
              <AINews />
            </PrivateRoute>
          ),
        },
        {
          path: "shock",
          element: (
            <PrivateRoute>
              <Shock />
            </PrivateRoute>
          ),
        },
        {
          path: "forecast",
          element: (
            <PrivateRoute>
              <DashboardMacroForecast />
            </PrivateRoute>
          ),
        },
        {
          path: "forecast/detail",
          element: (
            <PrivateRoute>
              <MacroForecast />
            </PrivateRoute>
          ),
        },
        {
          path: "outlook",
          element: (
            <PrivateRoute>
              <MacroOutlook />
            </PrivateRoute>
          ),
        },
        {
          path: "observations",
          element: (
            <PrivateRoute>
              <Observations />
            </PrivateRoute>
          ),
        },
        {
          path: "saturation",
          element: (
            <PrivateRoute>
              <SaturationDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "sector-momentum",
          element: (
            <MacroImpulseDashboard />
          ),
        },
        {
          path: "market-breadth",
          element: (
            <PrivateRoute>
              <Level1Dashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "stocks",
          element: (
            <PrivateRoute>
              <StocksList />
            </PrivateRoute>
          ),
        },
        {
          path: "stocks/:ticker",
          element: (
            <PrivateRoute>
              <StockDetail />
            </PrivateRoute>
          ),
        },
        {
          path: "recommendations",
          element: (
            <PrivateRoute>
              <Recommendations />
            </PrivateRoute>
          ),
        },
        {
          path: "scanner",
          element: (
            <PrivateRoute>
              <StockScanner />
            </PrivateRoute>
          ),
        },
        {
          path: "alerts",
          element: (
            <PrivateRoute>
              <Alerts />
            </PrivateRoute>
          ),
        },
        {
          path: "downtrend",
          element: (
            <PrivateRoute>
              <DowntrendDetection />
            </PrivateRoute>
          ),
        },
        {
          path: "sectors",
          element: (
            <PrivateRoute>
              <SectorsManagement />
            </PrivateRoute>
          ),
        },
        {
          path: "risk-management",
          element: (
            <PrivateRoute>
              <RiskManagement />
            </PrivateRoute>
          ),
        },
        {
          path: "trading-decision",
          element: (
            <PrivateRoute>
              <TradingDecision />
            </PrivateRoute>
          ),
        },
        {
          path: "downshift-scanner",
          element: (
            <PrivateRoute>
              <DownshiftScanner />
            </PrivateRoute>
          ),
        },
        {
          path: "easy-step",
          element: (
            <PrivateRoute>
              <EasyStepScanner />
            </PrivateRoute>
          ),
        },
        {
          path: "performance",
          element: (
            <PrivateRoute>
              <PerformanceDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "portfolio-health",
          element: (
            <PrivateRoute>
              <PortfolioHealth />
            </PrivateRoute>
          ),
        },
        {
          path: "portfolio-construction",
          element: (
            <PrivateRoute>
              <PortfolioConstruction />
            </PrivateRoute>
          ),
        },
        {
          path: "auto-pick",
          element: (
            <PrivateRoute>
              <AutoPick />
            </PrivateRoute>
          ),
        },
        {
          path: "portfolio-recommendations",
          element: (
            <PrivateRoute>
              <PortfolioRecommendations />
            </PrivateRoute>
          ),
        },
        {
          path: "safe-invest",
          element: (
            <PrivateRoute>
              <SafeInvest />
            </PrivateRoute>
          ),
        },
        {
          path: "statistical-risk",
          element: (
            <PrivateRoute>
              <StatisticalDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "regime-watchlist",
          element: (
            <PrivateRoute>
              <RegimeWatchlist />
            </PrivateRoute>
          ),
        },
        {
          path: "risk-opportunity",
          element: (
            <PrivateRoute>
              <RiskOpportunityMatrix />
            </PrivateRoute>
          ),
        },
        {
          path: "calibration",
          element: (
            <PrivateRoute>
              <CalibrationDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "paper-trading",
          element: (
            <PrivateRoute>
              <PaperTrading />
            </PrivateRoute>
          ),
        },
        {
          path: "detector-paper-trading",
          element: (
            <PrivateRoute>
              <DetectorPaperTrading />
            </PrivateRoute>
          ),
        },
        {
          path: "detector-recommendations",
          element: (
            <PrivateRoute>
              <DetectorRecommendations />
            </PrivateRoute>
          ),
        },
        {
          path: "high-vol-regime",
          element: (
            <PrivateRoute>
              <HighVolDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "reversal-catcher",
          element: (
            <PrivateRoute>
              <ReversalCatcherScanner />
            </PrivateRoute>
          ),
        },
        {
          path: "early-pivot",
          element: (
            <PrivateRoute>
              <EarlyPivotScanner />
            </PrivateRoute>
          ),
        },
        {
          path: "pivot-points",
          element: (
            <PrivateRoute>
              <PivotPoints />
            </PrivateRoute>
          ),
        },
        {
          path: "darvas-box",
          element: (
            <PrivateRoute>
              <DarvasBox />
            </PrivateRoute>
          ),
        },
        {
          path: "sector-rotation",
          element: (
            <PrivateRoute>
              <SectorRotation />
            </PrivateRoute>
          ),
        },
        {
          path: "icb-industry",
          element: (
            <PrivateRoute>
              <ICBIndustry />
            </PrivateRoute>
          ),
        },
        {
          path: "market-correction",
          element: (
            <PrivateRoute>
              <MarketCorrection />
            </PrivateRoute>
          ),
        },
        {
          path: "model-accuracy",
          element: (
            <PrivateRoute>
              <ModelAccuracy />
            </PrivateRoute>
          ),
        },
        {
          path: "market-recovery",
          element: (
            <PrivateRoute>
              <MarketRecovery />
            </PrivateRoute>
          ),
        },
        {
          path: "hedge-detection",
          element: (
            <PrivateRoute>
              <HedgeDetection />
            </PrivateRoute>
          ),
        },
        {
          path: "sentiment",
          element: (
            <PrivateRoute>
              <MarketSentiment />
            </PrivateRoute>
          ),
        },
        {
          path: "concentration",
          element: (
            <PrivateRoute>
              <ConcentrationAllocation />
            </PrivateRoute>
          ),
        },
        {
          path: "market-risk",
          element: (
            <PrivateRoute>
              <MarketRiskUnified />
            </PrivateRoute>
          ),
        },
        {
          path: "trend-predictions",
          element: (
            <PrivateRoute>
              <TrendPredictions />
            </PrivateRoute>
          ),
        },
        {
          path: "tplus",
          element: (
            <PrivateRoute>
              <TPlus />
            </PrivateRoute>
          ),
        },
        {
          path: "stock-phases",
          element: (
            <PrivateRoute>
              <StockPhases />
            </PrivateRoute>
          ),
        },
        {
          path: "pattern-cluster",
          element: (
            <PrivateRoute>
              <PatternCluster />
            </PrivateRoute>
          ),
        },
        {
          path: "risk-dashboard",
          element: (
            <PrivateRoute>
              <RiskMLDashboard />
            </PrivateRoute>
          ),
        },

        {
          path: "risk-detail/:ticker",
          element: (
            <PrivateRoute>
              <RiskMLDetail />
            </PrivateRoute>
          ),
        },
        {
          path: "upside-dashboard",
          element: (
            <PrivateRoute>
              <UpsideMLDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "upside-detail/:ticker",
          element: (
            <PrivateRoute>
              <UpsideMLDetail />
            </PrivateRoute>
          ),
        },
        {
          path: "legendary-strategies",
          element: (
            <PrivateRoute>
              <LegendaryHub />
            </PrivateRoute>
          ),
        },
        {
          path: "legendary-strategies/oneil",
          element: (
            <PrivateRoute>
              <LegendaryONeil />
            </PrivateRoute>
          ),
        },
        {
          path: "legendary-strategies/minervini",
          element: (
            <PrivateRoute>
              <LegendaryMinervini />
            </PrivateRoute>
          ),
        },
        {
          path: "legendary-strategies/weinstein",
          element: (
            <PrivateRoute>
              <LegendaryWeinstein />
            </PrivateRoute>
          ),
        },
        {
          path: "legendary-strategies/turtle",
          element: (
            <PrivateRoute>
              <LegendaryTurtle />
            </PrivateRoute>
          ),
        },
        {
          path: "legendary-strategies/livermore",
          element: (
            <PrivateRoute>
              <LegendaryLivermore />
            </PrivateRoute>
          ),
        },
        {
          path: "money-flow",
          element: (
            <PrivateRoute>
              <MoneyFlow />
            </PrivateRoute>
          ),
        },
        {
          path: "distribution-detector",
          element: (
            <PrivateRoute>
              <DistributionDetector />
            </PrivateRoute>
          ),
        },
        {
          path: "vpa",
          element: (
            <PrivateRoute>
              <VpaPage />
            </PrivateRoute>
          ),
        },
        {
          path: "vsa",
          element: (
            <PrivateRoute>
              <VsaPage />
            </PrivateRoute>
          ),
        },
        {
          path: "surge-readiness",
          element: (
            <PrivateRoute>
              <SurgeReadiness />
            </PrivateRoute>
          ),
        },
        {
          path: "stock-strength",
          element: (
            <PrivateRoute>
              <StockStrength />
            </PrivateRoute>
          ),
        },
        {
          path: "ichimoku-transition",
          element: (
            <PrivateRoute>
              <IchimokuTransition />
            </PrivateRoute>
          ),
        },
        {
          path: "breakout-candidates",
          element: (
            <PrivateRoute>
              <BreakoutCandidates />
            </PrivateRoute>
          ),
        },
        {
          path: "preparation-phase",
          element: (
            <PrivateRoute>
              <PreparationPhase />
            </PrivateRoute>
          ),
        },
        {
          path: "early-accumulation",
          element: (
            <PrivateRoute>
              <EarlyAccumulationDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "bottoming-flow",
          element: (
            <PrivateRoute>
              <BottomingFlowMonitor />
            </PrivateRoute>
          ),
        },
        {
          path: "market-internal-radar",
          element: (
            <PrivateRoute>
              <MarketInternalRadar />
            </PrivateRoute>
          ),
        },
        {
          path: "behavior",
          element: (
            <PrivateRoute>
              <BehaviorLabels />
            </PrivateRoute>
          ),
        },
        {
          path: "silent-accumulation",
          element: (
            <PrivateRoute>
              <SilentAccumulation />
            </PrivateRoute>
          ),
        },
        {
          path: "risk-composite",
          element: (
            <PrivateRoute>
              <RiskCompositeDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "niche-regime",
          element: (
            <PrivateRoute>
              <NicheRegimeDashboard />
            </PrivateRoute>
          ),
        },
        {
          path: "market-divergence",
          element: (
            <PrivateRoute>
              <MarketDivergence />
            </PrivateRoute>
          ),
        },
        {
          path: "market-resilience",
          element: (
            <PrivateRoute>
              <MarketResilience />
            </PrivateRoute>
          ),
        },
        {
          path: "smart-money-unified",
          element: (
            <PrivateRoute>
              <SmartMoneyUnified />
            </PrivateRoute>
          ),
        },
        {
          path: "duplicate-signature",
          element: (
            <PrivateRoute>
              <DuplicateSignature />
            </PrivateRoute>
          ),
        },
        {
          path: "backtest-validation",
          element: (
            <PrivateRoute>
              <BacktestValidation />
            </PrivateRoute>
          ),
        },
        {
          path: "smart-money-center",
          element: (
            <PrivateRoute>
              <SmartMoneyCommandCenter />
            </PrivateRoute>
          ),
        },
        {
          path: "early-divergence",
          element: (
            <PrivateRoute>
              <EarlyDivergenceWatchlist />
            </PrivateRoute>
          ),
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;


